import React, { useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import type { CohortMemberProgress, CohortRole } from '../../types';

import { avatarColor, initialsOf } from './utils';
import MemberActionsMenu, { ToastVariant } from './MemberActionsMenu';
import messages from './messages';

interface MemberCardProps {
  member: CohortMemberProgress;
  /** Resolves a course id to its localized chip label (cohort-locale config). */
  resolveCourseLabel: (courseId: string) => string;
  /** Cohort slug — required to render the actions menu. */
  slug?: string;
  /** The caller's own role, driving which actions the menu offers. */
  callerRole?: CohortRole;
  /** Reloads the current members page after a member mutation. */
  onMutated?: (wasRemoval: boolean) => void;
  /** Raises a member-action toast on the stable parent view. */
  onToast?: (variant: ToastVariant, msg: string) => void;
}

/**
 * One member row in the cohort Members view. The owner shows a Facilitator
 * badge and no progress; others show a progress bar and expandable chip panel.
 */
const MemberCard: React.FC<MemberCardProps> = ({
  member, resolveCourseLabel, slug, callerRole, onMutated, onToast,
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);

  const { progress } = member;
  const isOwner = member.role === 'owner';
  const isCoFacilitator = member.role === 'co_facilitator';
  const displayName = member.name || member.username;

  let badgeLabel: string | null = null;
  if (isOwner) {
    badgeLabel = intl.formatMessage(messages.badgeFacilitator);
  } else if (isCoFacilitator) {
    badgeLabel = intl.formatMessage(messages.roleCoFacilitator);
  }

  const percent = progress && progress.total_count > 0
    ? Math.round((progress.completed_count / progress.total_count) * 100)
    : 0;

  return (
    <li className="cohort-members-card">
      <div className="cohort-members-card-row">
        <div
          className="cohort-members-avatar"
          style={{ backgroundColor: avatarColor(member.username || displayName) }}
          aria-hidden="true"
        >
          {initialsOf(displayName)}
        </div>

        <div className="cohort-members-identity">
          <div className="cohort-members-name">
            <span>{displayName}</span>
            {badgeLabel && (
              <span className={`cohort-members-badge${isOwner ? ' cohort-members-badge-owner' : ''}`}>
                {badgeLabel}
              </span>
            )}
          </div>
          <div className="cohort-members-joined">
            {intl.formatMessage(messages.memberJoinedLabel, {
              date: intl.formatDate(member.joined, { year: 'numeric', month: 'long', day: 'numeric' }),
            })}
          </div>
        </div>

        {progress && (
          <div className="cohort-members-progress">
            <span className="cohort-members-progress-count">
              {intl.formatMessage(messages.progressModulesCount, {
                completed: progress.completed_count,
                total: progress.total_count,
              })}
            </span>
            <div className="cohort-members-bar" aria-hidden="true">
              <div className="cohort-members-bar-fill" style={{ width: `${percent}%` }} />
            </div>
            <button
              type="button"
              className="cohort-members-chevron"
              aria-expanded={expanded}
              aria-label={intl.formatMessage(
                expanded ? messages.progressToggleCollapse : messages.progressToggleExpand,
              )}
              onClick={() => setExpanded((open) => !open)}
            >
              <span aria-hidden="true">{expanded ? '▲' : '▼'}</span>
            </button>
          </div>
        )}

        {/* The kebab menu decides its own visibility and may render nothing. */}
        <div className="cohort-members-actions">
          {slug && callerRole && onMutated && onToast && (
            <MemberActionsMenu
              slug={slug}
              member={member}
              callerRole={callerRole}
              onMutated={onMutated}
              onToast={onToast}
            />
          )}
        </div>
      </div>

      {progress && expanded && (
        <div className="cohort-members-modules">
          <div className="cohort-members-modules-title">
            {intl.formatMessage(messages.progressTitle)}
          </div>
          <ul className="cohort-members-chips">
            {progress.courses.map((course) => (
              <li
                key={course.course_id}
                className={`cohort-members-chip${course.completed ? ' cohort-members-chip-complete' : ''}`}
              >
                <span
                  className="cohort-members-chip-icon"
                  role="img"
                  aria-label={intl.formatMessage(
                    course.completed ? messages.progressCompleted : messages.progressNotCompleted,
                  )}
                >
                  {course.completed ? '✓' : '○'}
                </span>
                <span className="cohort-members-chip-label">
                  {resolveCourseLabel(course.course_id)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

export default MemberCard;
